// Grids & Gantts — creating work INTO the structure, along the
// architectural hierarchy.
//
// A structure page that can only read is half a page. The "+" on a row
// creates the work that belongs under it — and "belongs" is not a guess:
// the site's own creation tree says which work types may hang under which,
// and by what relationship (a real child, or a link of a named type in a
// named direction). This asks that tree, offers only what it allows, and
// wires the new item up the way the tree says to.
//
// THE TREE RULES ARE NOT RE-IMPLEMENTED HERE. `getAllowedChildTypes` and
// `resolveHierarchyCreateOptions` are pure functions that already encode
// them, and a second copy would be a second place for the rules to drift.
// They are imported, not forked — this file is the loading, the caching and
// the two write calls around them.
//
// Caches are module-level and deliberate: a structure has hundreds of rows
// across a handful of projects, and the scheme → tree → types chain would
// otherwise be re-fetched for every "+" the reader hovers.

import { LINK_TYPE_NAME } from '@client/config/client.config';
import { client } from '@platform/http/api-client';
import { getAllowedChildTypes, resolveHierarchyCreateOptions } from '@modules/folders/frontend/utils/treeHelpers';
import { byKeyOrder, cloneEdgeOf, holdingTypes, isChildEdge, parentSearchZql } from './clone';

const treeByProject = new Map();     // project key → tree | null
// MANY PROJECTS SHARE ONE SCHEME, and the tree belongs to the scheme. Cached
// per project alone, a structure over fifty projects on one scheme fetched
// the same tree fifty times on every open (measured: 50 identical requests
// per load). The scheme's tree is asked for once and every project on it
// reads the same promise.
const treeByScheme = new Map();      // scheme id → Promise<tree | null>
const typesByProject = new Map();    // project key → issue types
const pending = new Map();           // project key → in-flight promise
const pendingTypes = new Map();      // project key → in-flight type list
// Projects whose tree could not be READ — not the same as having none. The
// "+" stays on their rows: a read that failed is no reason to hide it.
const unread = new Set();

export const projectOf = (item) => (item && item.fields && item.fields.project && item.fields.project.key)
  || String((item && item.key) || '').split('-')[0] || '';

// The project's numeric id, which the create screen is loaded by. It rides
// on the work item's project field; when a slimmed row lacks it, the
// project list is asked once and remembered.
const projectIds = new Map();
export async function projectIdOf(api, item) {
  const own = item && item.fields && item.fields.project && item.fields.project.id;
  if (own) return String(own);
  const key = projectOf(item);
  if (!key) return null;
  if (!projectIds.has(key)) {
    projectIds.set(key, (async () => {
      try {
        const list = await api.getProjects();
        const hit = (Array.isArray(list) ? list : (list && list.values) || []).find((p) => p && p.key === key);
        return hit && hit.id != null ? String(hit.id) : null;
      } catch { return null; }
    })());
  }
  return projectIds.get(key);
}
export const typeNameOf = (item) => (item && item.fields && item.fields.issuetype && item.fields.issuetype.name) || '';

// The project's creation tree, once per project per session. A project
// whose scheme carries no tree answers null — that is a plain "no
// hierarchy here", not an error, and the "+" simply stays quiet.
export async function loadTree(api, projectKey) {
  if (!projectKey) return null;
  if (treeByProject.has(projectKey)) return treeByProject.get(projectKey);
  if (pending.has(projectKey)) return pending.get(projectKey);
  const p = (async () => {
    let tree = null;
    try {
      const schemes = await api.getProjectIssueTypeSchemes([projectKey]);
      const schemeId = schemes && schemes[projectKey] && schemes[projectKey].schemeId;
      if (schemeId) {
        if (!treeByScheme.has(schemeId)) {
          treeByScheme.set(schemeId, api.getCreationTreeByScheme(schemeId)
            .then((found) => (found && found.treeData) || null)
            .catch(() => undefined));
        }
        tree = await treeByScheme.get(schemeId);
        if (tree === undefined) { unread.add(projectKey); tree = null; }
      }
    } catch { tree = null; unread.add(projectKey); }
    treeByProject.set(projectKey, tree);
    pending.delete(projectKey);
    return tree;
  })();
  pending.set(projectKey, p);
  return p;
}

// MANY PROJECTS AT ONCE. The schemes endpoint takes a list; asking it for
// one project at a time over a structure of two hundred projects was two
// hundred round trips before the parent picker could say anything. One
// call answers every scheme, and each distinct scheme's tree is read once.
export async function loadTrees(api, projectKeys) {
  const keys = [...new Set((projectKeys || []).filter(Boolean))];
  const missing = keys.filter((k) => !treeByProject.has(k) && !pending.has(k));
  if (missing.length) {
    const p = (async () => {
      let schemes = {};
      try { schemes = (await api.getProjectIssueTypeSchemes(missing)) || {}; } catch { schemes = {}; missing.forEach((k) => unread.add(k)); }
      await Promise.all(missing.map(async (k) => {
        const schemeId = schemes[k] && schemes[k].schemeId;
        let tree = null;
        if (schemeId) {
          if (!treeByScheme.has(schemeId)) {
            treeByScheme.set(schemeId, api.getCreationTreeByScheme(schemeId)
              .then((found) => (found && found.treeData) || null)
              .catch(() => undefined));
          }
          tree = await treeByScheme.get(schemeId);
          if (tree === undefined) { unread.add(k); tree = null; }
        }
        treeByProject.set(k, tree);
      }));
    })();
    missing.forEach((k) => pending.set(k, p.then(() => treeByProject.get(k) || null)));
    await p;
    missing.forEach((k) => pending.delete(k));
  }
  return new Map(await Promise.all(keys.map(async (k) => [k, await loadTree(api, k)])));
}

// Every row of a project asks at once when the grid opens, so they share the
// one request in flight rather than each making their own.
export async function loadTypes(api, projectKey) {
  if (!projectKey) return [];
  if (typesByProject.has(projectKey)) return typesByProject.get(projectKey);
  if (!pendingTypes.has(projectKey)) {
    pendingTypes.set(projectKey, api.getProjectIssueTypes(projectKey).catch(() => []).then((types) => {
      typesByProject.set(projectKey, types || []);
      pendingTypes.delete(projectKey);
      return types || [];
    }));
  }
  return pendingTypes.get(projectKey);
}

// What may be created under this work item, narrowed to the types the
// project actually offers. An empty answer means the hierarchy allows
// nothing here — say so rather than offering a free-for-all.
export async function childOptionsFor(api, item) {
  const projectKey = projectOf(item);
  const typeName = typeNameOf(item);
  if (!projectKey || !typeName) return [];
  // THE TWO LOADS DO NOT DEPEND ON EACH OTHER. The tree says what may hang
  // under this work type; the type list says which of those the project
  // actually offers. Fetching the tree, then deciding, then fetching the
  // types made three round trips of a menu that should feel instant — and
  // on a proxied dev site that was four seconds of staring at "reading the
  // hierarchy…". They go together now.
  const [tree, types] = await Promise.all([loadTree(api, projectKey), loadTypes(api, projectKey)]);
  if (!tree) return [];
  if (!getAllowedChildTypes(typeName, tree, LINK_TYPE_NAME).length) return [];
  return resolveHierarchyCreateOptions(typeName, tree, types, LINK_TYPE_NAME);
}

// NOTHING MAY BE CREATED UNDER THIS WORK ITEM — so its row draws no "+".
// True only when that is KNOWN: a tree or a type list that could not be read
// answers false, and the "+" stays. Reads what childOptionsFor reads, from
// the same per-project caches the grid already fills when it opens.
export async function nothingToCreate(api, item) {
  const projectKey = projectOf(item);
  if (!projectKey || !typeNameOf(item)) return false;
  const [options, types] = await Promise.all([childOptionsFor(api, item), loadTypes(api, projectKey)]);
  if (unread.has(projectKey) || !types.length) return false;
  return options.length === 0;
}

// WHICH WORK ITEMS ON THE PAGE MAY HOLD A COPY of the given types — the
// parents a clone may be given. The creation tree of each candidate's own
// project says what may hang under its type and by what edge; a candidate
// must accept EVERY type in `childTypeNames` (the roots being cloned may be
// of several), and the edge is answered per child type, since an epic may
// hold a task as a real child and a module by a link. `projectKey` narrows
// the candidates to one project — the rule when the copies change project.
// Answered off the page's own rows, so a parent picked here is a parent
// the page can show the copies under.
const lower = (s) => String(s || '').trim().toLowerCase();
export async function parentCandidates(api, items, childTypeNames, projectKey = null) {
  const want = [...new Set((childTypeNames || []).map(lower).filter(Boolean))];
  if (!want.length) return [];
  const list = (items || []).filter((it) => it && it.key && (!projectKey || projectOf(it) === projectKey));
  const trees = await loadTrees(api, list.map(projectOf));
  const edgesOf = new Map();   // "PROJ:type" → Map(child type → edge)
  const out = [];
  list.forEach((it) => {
    const p = projectOf(it);
    const tree = trees.get(p);
    const tn = typeNameOf(it);
    if (!tree || !tn) return;
    const ck = `${p}:${lower(tn)}`;
    if (!edgesOf.has(ck)) {
      edgesOf.set(ck, new Map(getAllowedChildTypes(tn, tree, LINK_TYPE_NAME).map((e) => [lower(e.issueTypeName), cloneEdgeOf(e, LINK_TYPE_NAME)])));
    }
    const allowed = edgesOf.get(ck);
    if (!want.every((w) => allowed.has(w))) return;
    const edgeByType = {};
    want.forEach((w) => { edgeByType[w] = allowed.get(w); });
    out.push({ key: it.key, item: it, typeName: tn, projectKey: p, edgeByType });
  });
  return out;
}

// THE PARENTS A COPY MAY BE GIVEN, SEARCHED ACROSS STRIDE. The target
// project's creation tree says which of its work types may hold the copies
// (holdingTypes in ./clone.js); Zebra Search then answers the work items of
// those types in that project — matching `text` by key or summary when the
// reader typed something — with the reader's own browse permission. The
// grid's rows are no longer the limit: a clone is made in Stride, not in a
// grid. Answers { list: [{ key, item, typeName, projectKey, edgeByType }],
// more, types } — `more` when there are past `limit`, `types` the type names
// that may hold the copies at all (empty = the hierarchy allows nothing).
export async function searchParents(api, { projectKey, childTypeNames, text = '', limit = 50 } = {}) {
  if (!projectKey) return { list: [], more: false, types: [] };
  const [tree, types] = await Promise.all([loadTree(api, projectKey), loadTypes(api, projectKey)]);
  if (!tree) return { list: [], more: false, types: [] };
  const holders = holdingTypes((types || []).map((t) => t && t.name), (tn) => getAllowedChildTypes(tn, tree, LINK_TYPE_NAME), childTypeNames, LINK_TYPE_NAME);
  if (!holders.length) return { list: [], more: false, types: [] };
  const res = await api.searchIssues(parentSearchZql(projectKey, holders.map((h) => h.typeName), text),
    ['summary', 'issuetype', 'project', 'status'], limit + 1, null, 0, true);
  const found = (res && res.issues) || [];
  const edgesOf = new Map(holders.map((h) => [lower(h.typeName), h.edgeByType]));
  const list = found.slice(0, limit)
    .map((it) => ({ key: it.key, item: it, typeName: typeNameOf(it), projectKey, edgeByType: edgesOf.get(lower(typeNameOf(it))) }))
    .filter((c) => c.edgeByType)
    .sort(byKeyOrder);
  return { list, more: found.length > limit, types: holders.map((h) => h.typeName) };
}

// Warm a project's hierarchy before anybody asks for it — on hover, or for
// the work on screen once the structure lands. Everything is cached per
// project, so this costs one pair of requests per project, ever.
export function warmHierarchy(api, item) {
  const projectKey = projectOf(item);
  if (!projectKey || treeByProject.has(projectKey) || pending.has(projectKey)) return;
  loadTree(api, projectKey).catch(() => {});
  loadTypes(api, projectKey).catch(() => {});
}

// The projects visible in a set of rows, warmed once.
export function warmProjects(api, items) {
  const seen = new Set();
  (items || []).forEach((it) => {
    const k = projectOf(it);
    if (!k || seen.has(k)) return;
    seen.add(k);
    warmHierarchy(api, it);
  });
}

// Create one work item under `parent`, on the edge the tree named.
//
// A real child carries `parent` on the create itself. A linked child is
// created first and joined second, and the direction matters: the edge's
// `linkDirection` is the phrase the CHILD displays, and the create call
// puts the outward-phrase item in `inwardIssue`. Getting that backwards
// makes a child that the structure's own extender cannot see.
//
// IN THREE PIECES, because the create can fail halfway for a reason the
// reader can fix. A workflow with a validator on Create — "Client Name must
// be filled in" — refuses the bare summary with a 400 naming the fields it
// wants. The product's own answer to that is the validator-recovery form,
// which collects those fields and RETRIES THE CREATE ITSELF; what it hands
// back is the created item, and the link still has to be made. So the fields
// are built by one function, the link by another, and `createChild` is the
// two of them in a row for the day the workflow asks for nothing.
// The rule itself lives in ./clone.js (pure, under test); it is the same
// rule for a "+" and for a copy.
export { isChildEdge };

export function createFields({ parent, option, summary }) {
  const fields = {
    project: { key: projectOf(parent) },
    issuetype: option && option.issueTypeId ? { id: String(option.issueTypeId) } : { name: option.issueTypeName },
    summary: String(summary || '').trim(),
  };
  if (isChildEdge(option && option.edge)) fields.parent = { key: parent.key };
  return fields;
}

export const keyOfCreated = (created) => (created && (created.key || (created.issue && created.issue.key))) || '';

export async function linkNew(api, { parent, edge, newKey }) {
  const e = edge || {};
  const type = e.linkTypeId
    ? { id: String(e.linkTypeId), name: e.linkTypeName || LINK_TYPE_NAME }
    : { name: e.linkTypeName || LINK_TYPE_NAME };
  const link = e.linkDirection === 'inward'
    ? { type, inwardIssue: { key: parent.key }, outwardIssue: { key: newKey } }
    : { type, inwardIssue: { key: newKey }, outwardIssue: { key: parent.key } };
  await api.linkIssue(link);
}

// After the item exists — by whichever route — it is joined to its parent
// the way the edge says, and the page is told what was made.
export async function finishCreate(api, { parent, option, created }) {
  const edge = (option && option.edge) || {};
  const isChild = isChildEdge(edge);
  const newKey = keyOfCreated(created);
  if (!newKey) throw new Error('The work item was created but the server did not name it');
  if (!isChild) await linkNew(api, { parent, edge, newKey });
  return { key: newKey, isChild, edge };
}

export async function createChild(api, { parent, option, summary }) {
  const fields = createFields({ parent, option, summary });
  const created = await api.createIssue({ fields });
  return finishCreate(api, { parent, option, created });
}

// WHAT THE WORKFLOW WILL ASK FOR, before anybody is refused. The create gate
// can be dry-run: the same validators, no write, answering with the fields
// they want filled. Asked the moment a work type is picked, so the reader
// sees "this also needs a Client Name" beside the summary box rather than as
// a red line after pressing Create. Fail-open, like the gate itself: if the
// preflight cannot be read, the create is simply tried.
export async function preflightCreate({ parent, option, summary }) {
  const fields = createFields({ parent, option, summary });
  const item = { projectKey: projectOf(parent), issuetype: fields.issuetype, fields };
  if (fields.parent) item.parentRef = { mode: 'KEY', value: parent.key };
  try {
    const r = await client.post('/api/issue/create-preflight', { items: [item] });
    const blockers = (r && r.data && Array.isArray(r.data.blockers)) ? r.data.blockers : [];
    return blockers.map((b) => ({ name: b.name || '', message: b.message || '', fields: Array.isArray(b.fields) ? b.fields : [] }));
  } catch { return []; }
}

// The 400 a validator answers with, if that is what an error is.
export function validatorRefusal(err) {
  const d = err && err.response && err.response.data && err.response.data.details;
  if (!d || typeof d !== 'object') return null;
  const messages = Array.isArray(d.errorMessages) ? d.errorMessages : [];
  const missing = Array.isArray(d.missingFields) ? d.missingFields : [];
  return messages.length || missing.length ? { messages, missing } : null;
}
